import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
	AppDispatch,
	RootState, 
} from '@/store/store';

import { 
	PL_TYPES_ALL,
	PL_TYPES_ENABLED,
	PL_TYPES_MANAGED,
	PL_TYPES_MENU,
} from '@/constants/product';

import ProductLine from '@/models/product-lines/ProductLine';

import { getOrderedMenu } from '@/utils/product';

import {
	productLinesFetchAll,
	productLinesFetchManaged,
	productLineFetchMenus,
	productLineFetchEnabled,
} from '@/actions/product-line';


const TYPE_MAPPINGS = {
	[PL_TYPES_ALL]: {
		linesKey: 'productLines',
		pendingKey: 'productLinesPending',
		fetchMethod: productLinesFetchAll,
	},
	[PL_TYPES_ENABLED]: {
		linesKey: 'enabled',
		pendingKey: 'enabledPending',
		fetchMethod: productLineFetchEnabled,
	},
	[PL_TYPES_MANAGED]: {
		linesKey: 'productLinesManaged',
		pendingKey: 'productLinesManagedPending',
		fetchMethod: productLinesFetchManaged,
	},
	[PL_TYPES_MENU]: {
		linesKey: 'menus',
		pendingKey: 'menusPending',
		fetchMethod: productLineFetchMenus,
	},
};

const useProductLines = (type: keyof typeof TYPE_MAPPINGS) => {

	// Mappings
	const typeMapped = TYPE_MAPPINGS[type as keyof typeof TYPE_MAPPINGS] || TYPE_MAPPINGS[PL_TYPES_ALL];

	// Dispatch
	const dispatch = useDispatch<AppDispatch>();
	
	// Access Redux state
	const productLinesRedux = useSelector((state: RootState) => state.productLine[typeMapped.linesKey]);
	const productLinesPendingRedux = useSelector((state: RootState) => state.productLine[typeMapped.pendingKey]);

	// Local state for hydration handling
	const [hydratedLines, setHydratedLines] = useState<ProductLine[]>([]);
	const [hydratedLinesPending, setHydratedLinesPending] = useState<boolean>(false);

	// Methods
	const callFetch = useCallback(async () => {
		try {
			dispatch(typeMapped.fetchMethod());
		} catch(err) {
			err && console.error(err);
		}
	}, [ dispatch ])
  
	// Effects
	useEffect(() => {
		if(type === PL_TYPES_MENU) {
			// Auto-order menu product lines
			setHydratedLines(getOrderedMenu(productLinesRedux || []));
		} else {
			// Default
			setHydratedLines(productLinesRedux || []);
		}
	}, [ productLinesRedux ]);

	useEffect(() => {
		setHydratedLinesPending(!!productLinesPendingRedux);
	}, [ productLinesPendingRedux ]);

	return {
		lines: hydratedLines,
		linesPending: hydratedLinesPending,
		fetchLines: callFetch,
	};
};
export default useProductLines;